import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { SERVICES } from "../lib/content";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";

const STEPS = ["Identity", "Project", "Strategy", "Review"];

const BUDGETS = ["Under $5,000", "$5,000–$15,000", "$15,000–$50,000", "$50,000–$150,000", "$150,000+"];

export default function IntakePage() {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", organization: "", role_title: "",
    service_type: SERVICES[0].title, budget_range: BUDGETS[1], event_dates: "",
    project_goals: "", brand_voice: "", target_audience: "", timeline: "",
    referral_source: "", additional_notes: "",
  });

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/intake", form);
      toast.success("Intake received — the studio will be in touch shortly.");
      setDone(true);
    } catch {
      toast.error("Could not submit intake.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <section className="max-w-[800px] mx-auto px-6 py-32 text-center" data-testid="intake-success">
        <Check size={42} style={{ color: "var(--crest-gold)" }} className="mx-auto" />
        <h1 className="font-serif text-5xl tracking-tight mt-8">Intake received.</h1>
        <p className="text-[var(--crest-muted)] mt-6">We'll review your details and follow up within one business day.</p>
        <Link to="/" className="btn-primary mt-10 inline-flex">Back Home</Link>
      </section>
    );
  }

  return (
    <div data-testid="intake-page">
      <section className="max-w-[1100px] mx-auto px-6 md:px-10 pt-28 md:pt-40 pb-12">
        <div className="eyebrow text-[var(--crest-gold)]">— Client Intake Form</div>
        <h1 className="font-serif text-5xl md:text-7xl tracking-tight mt-6 leading-[1.02]">Tell us about your engagement.</h1>
        <div className="mt-12 flex gap-3 items-center" data-testid="intake-stepper">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-3">
              <div className={`w-7 h-7 flex items-center justify-center text-[11px] tracking-widest ${i <= step ? "bg-[var(--crest-black)] text-[var(--crest-cream)]" : "border border-black/20 text-[var(--crest-muted)]"}`}>
                {i + 1}
              </div>
              <div className={`text-[11px] tracking-[0.3em] uppercase ${i === step ? "text-[var(--crest-gold)]" : "text-[var(--crest-muted)]"}`}>{s}</div>
              {i < STEPS.length - 1 && <div className="w-12 h-px bg-black/15" />}
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-[1100px] mx-auto px-6 md:px-10 pb-32">
        <div className="border border-black/10 bg-white p-8 md:p-12">
          {step === 0 && (
            <div className="grid md:grid-cols-2 gap-x-10 gap-y-2" data-testid="intake-step-0">
              <h2 className="font-serif text-3xl md:col-span-2 mb-4">Identity</h2>
              <input className="crest-input md:col-span-2" placeholder="Full Name" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="intake-name" />
              <input type="email" className="crest-input" placeholder="Email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="intake-email" />
              <input className="crest-input" placeholder="Phone" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="intake-phone" />
              <input className="crest-input" placeholder="Organization" value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} data-testid="intake-org" />
              <input className="crest-input" placeholder="Role / Title" value={form.role_title} onChange={(e) => setForm({ ...form, role_title: e.target.value })} data-testid="intake-role" />
            </div>
          )}
          {step === 1 && (
            <div className="grid md:grid-cols-2 gap-x-10 gap-y-2" data-testid="intake-step-1">
              <h2 className="font-serif text-3xl md:col-span-2 mb-4">Project</h2>
              <div>
                <div className="eyebrow text-[var(--crest-muted)] mb-2">Service of Interest</div>
                <select className="crest-input" value={form.service_type} onChange={(e) => setForm({ ...form, service_type: e.target.value })} data-testid="intake-service">
                  {SERVICES.map((s) => <option key={s.id}>{s.title}</option>)}
                </select>
              </div>
              <div>
                <div className="eyebrow text-[var(--crest-muted)] mb-2">Budget Range</div>
                <select className="crest-input" value={form.budget_range} onChange={(e) => setForm({ ...form, budget_range: e.target.value })} data-testid="intake-budget">
                  {BUDGETS.map((b) => <option key={b}>{b}</option>)}
                </select>
              </div>
              <input className="crest-input md:col-span-2" placeholder="Event / Project Dates (if applicable)" value={form.event_dates} onChange={(e) => setForm({ ...form, event_dates: e.target.value })} data-testid="intake-dates" />
              <textarea className="crest-input md:col-span-2" placeholder="Project Goals" required value={form.project_goals} onChange={(e) => setForm({ ...form, project_goals: e.target.value })} data-testid="intake-goals" />
            </div>
          )}
          {step === 2 && (
            <div className="grid md:grid-cols-2 gap-x-10 gap-y-2" data-testid="intake-step-2">
              <h2 className="font-serif text-3xl md:col-span-2 mb-4">Strategy</h2>
              <textarea className="crest-input md:col-span-2" placeholder="Brand voice / tone" value={form.brand_voice} onChange={(e) => setForm({ ...form, brand_voice: e.target.value })} data-testid="intake-voice" />
              <textarea className="crest-input md:col-span-2" placeholder="Target audience" value={form.target_audience} onChange={(e) => setForm({ ...form, target_audience: e.target.value })} data-testid="intake-audience" />
              <input className="crest-input" placeholder="Timeline" value={form.timeline} onChange={(e) => setForm({ ...form, timeline: e.target.value })} data-testid="intake-timeline" />
              <input className="crest-input" placeholder="How did you hear about us?" value={form.referral_source} onChange={(e) => setForm({ ...form, referral_source: e.target.value })} data-testid="intake-referral" />
              <textarea className="crest-input md:col-span-2" placeholder="Additional notes" value={form.additional_notes} onChange={(e) => setForm({ ...form, additional_notes: e.target.value })} data-testid="intake-notes" />
            </div>
          )}
          {step === 3 && (
            <div data-testid="intake-step-3">
              <h2 className="font-serif text-3xl mb-6">Review & Submit</h2>
              <dl className="text-sm grid md:grid-cols-2 gap-x-10 gap-y-4">
                {Object.entries(form).map(([k, v]) => (
                  <div key={k}>
                    <dt className="text-[var(--crest-muted)] text-xs tracking-widest uppercase">{k.replace(/_/g, " ")}</dt>
                    <dd className="font-serif text-lg mt-1 break-words">{v || "—"}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          <div className="mt-10 flex justify-between items-center">
            <button onClick={back} disabled={step === 0} className="btn-secondary disabled:opacity-30" data-testid="intake-back">
              <ArrowLeft size={14} /> Back
            </button>
            {step < STEPS.length - 1 ? (
              <button onClick={next} className="btn-primary" data-testid="intake-next">Next <ArrowRight size={14} /></button>
            ) : (
              <button onClick={submit} disabled={submitting} className="btn-primary" data-testid="intake-submit">
                {submitting ? "Submitting..." : "Submit Intake"}
              </button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
